/**
 * CSS selectors for the nf-platform markup the collectors hook into.
 *
 * Every entry below was checked against nf-platform on 2026-08-11 and matches at least
 * one real element, except the ones explicitly marked RESERVED. That distinction matters:
 * an earlier version of this file listed invented BEM classes (`.nf-upsells__item`,
 * `.nf-landing__packages`, `.nf-ap-decline`, …) that matched nothing, so the events bound
 * to them reported zero and looked like unused features. Do not add a selector here
 * without opening the view file and confirming the class exists.
 *
 * Markup changes more often than tracking logic, so every entry can be overridden from
 * PHP with the `trackdaddy_tracker_selectors` filter — no rebuild needed.
 */

export const DEFAULT_SELECTORS = {
  // Landing one-pagers (spec §2).
  // src/Modules/Landing/Views/landing/{option-1,option-2,option-1-qty-merch}.php
  landingContainer: '.nf-landing, .nf-lqty-merch, [data-nf-landing]',

  // The three landing layouts each name their pack block differently
  // (.nf-landing-package / .nf-landing__tab / .nf-lqty-merch-pack), but all three carry
  // data-qty and data-price, and nf-platform's own landing.js drives them through
  // `container.querySelectorAll("[data-qty]")`. Binding to the attribute rather than the
  // classes covers every layout and survives the next redesign.
  landingOfferSection: '.nf-landing [data-qty], .nf-lqty-merch [data-qty], [data-nf-landing] [data-qty]',
  landingOption: '.nf-landing [data-qty], .nf-lqty-merch [data-qty], [data-nf-landing] [data-qty]',

  // landing.js adds this to whichever [data-qty] block is selected, in all three layouts.
  landingOptionActive: '.nf-landing__option--active',

  // The real WooCommerce checkout, embedded into the one-pager by the page builder.
  // templates/woocommerce/checkout/form-checkout.php renders form.checkout.nf-checkout.
  embeddedCheckout: 'form.checkout, [data-nf-checkout-embedded]',

  // Offers: after-purchase upsells and checkout/side-cart cross-sells (spec §1 + §4).
  // .nf-ap-offer          src/Modules/AfterPurchase/Views/_offer-card.php
  // .nf-ap-landing-card   src/Modules/AfterPurchase/Views/_landing-upsell-card.php
  // .nf-upsell-card       src/Modules/Offers/Views/{upsells,sidecart-upsells}.php
  offer: '.nf-ap-offer, .nf-ap-landing-card, .nf-upsell-card, [data-td-offer]',

  // .nf-ap-accept is on both after-purchase accept buttons; .nf-upsell-add is the
  // cross-sell one.
  offerAccept: '.nf-ap-accept, .nf-upsell-add, [data-td-offer-accept]',

  // "No thanks" / "I don't want this offer". On the after-purchase landing card this sits
  // inside the card; in container.php it sits in a sibling .nf-ap-controls section and
  // declines the whole phase at once — collectors/offers.js handles both shapes.
  offerDecline: '.nf-ap-skip, [data-td-offer-decline]',

  // The container a phase-level decline applies to.
  offerPhaseContainer: '#nf-ap',

  // Generic disclosure patterns plus the escape hatch. RESERVED: nf-platform has no
  // accordion inside an offer today, so offer_details_expanded stays inert until it does.
  offerDetails: '.nf-ap-offer details summary, .nf-upsell-card details summary, [data-td-offer-details]',

  // RESERVED: after-purchase quantity is hard-coded to 1 (after-purchase.js `const qty = 1`)
  // and the .nf-ap-qty handler is dead code, so offer_quantity_changed cannot fire yet.
  // The class contract is kept so it starts working the day a selector is added.
  offerQuantity: '.nf-ap-qty, [data-td-offer-qty]',

  // RESERVED: offers resolve to a fixed product id via ACF, there is no variant picker.
  // On landings the equivalent signal arrives as the nf:lp:option-selected event instead.
  offerOption: '[data-td-offer-option]',

  // Subscription popup — the ATC widget v2 modal (spec §5).
  // src/Modules/ProductPageWidgets/views/add-to-cart-v2.php
  subscriptionWidget: '.nf-atc2',
  subscriptionModal: '[data-nf-sub-modal]',
  // The modal select ONLY. The widget renders a second, inline select with the same
  // .nf-atc2__period class higher up the page (add-to-cart-v2.php:331), and querySelector
  // returns document order — so a looser selector reads the inline one and, worse, fires
  // subscription_popup_interval_selected when a customer changes the inline interval with
  // no popup open at all. atc-widget-v2.js:288-297 keeps the two in sync, so narrowing
  // loses no information.
  subscriptionInterval: 'select.nf-atc2__period--modal',
  subscriptionAccept: '.nf-atc2__sub-modal-yes',
  subscriptionDecline: '.nf-atc2__sub-modal-onetime',
  subscriptionDismiss: '.nf-atc2__sub-modal-close, .nf-atc2__sub-modal-backdrop',

  // Checkout.
  shippingMethod: 'input[name^="shipping_method"], select[name^="shipping_method"]',
  paymentMethod: 'input[name="payment_method"]',

  // Account and campaign pages (spec §6). Login, password-reset completion, reviews and
  // the birthday form are all reported from PHP, where the outcome is actually known —
  // only the reset request and the search box need a browser selector.
  // Both WooCommerce reset forms share these classes; the request form is the one without
  // input[name="reset_key"].
  lostPasswordForm: 'form.woocommerce-ResetPassword, form.lost_reset_password',
  searchForm: 'form.nf-site-header__search, form[role="search"], form.search-form',
  personalizationComplete: '[data-td-personalization-complete], .personalized-result',
};

/**
 * Shallow merge: an override replaces the default for that key outright, it is not
 * merged into it. Every value here is a complete selector string, so a filter that
 * wants to add a selector has to repeat the ones it still needs.
 */
export function resolveSelectors(overrides) {
  return Object.assign({}, DEFAULT_SELECTORS, overrides || {});
}
